/**
 * Sistema de almacenamiento local
 * Maneja persistencia de datos usando localStorage
 */

import { generateId } from '../utils/routeros.js';

// Claves de almacenamiento
const STORAGE_KEYS = {
  BALANCEADOR_CONFIGS: 'balanceador_configs',
  HOTSPOT_CONFIGS: 'hotspot_configs',
  FAILOVER_CONFIGS: 'failover_configs',
  QOS_CONFIGS: 'qos_configs',
  PORTAL_CONFIGS: 'portal_configs',
  FICHAS_PROFILES: 'fichas_profiles',
  SCRIPT_HISTORY: 'script_history',
  LICENSES: 'licenses'
};

/**
 * Obtiene datos de localStorage
 * @param {string} key - Clave de almacenamiento
 * @returns {Array} Datos o array vacío
 */
function getFromStorage(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error(`Error al leer ${key} de localStorage:`, error);
    return [];
  }
}

/**
 * Guarda datos en localStorage
 * @param {string} key - Clave de almacenamiento
 * @param {Array} data - Datos a guardar
 */
function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error al guardar ${key} en localStorage:`, error);
    throw new Error('Error guardando datos. El almacenamiento puede estar lleno.');
  }
}

// ==================== CONFIGURACIONES ====================

/**
 * Guarda configuración de balanceador
 * @param {object} config - Configuración
 * @returns {object} Configuración guardada con ID
 */
export function saveBalanceadorConfig(config) {
  const configs = getFromStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS);
  const newConfig = {
    ...config,
    id: generateId(),
    createdAt: new Date().toISOString()
  };
  configs.push(newConfig);
  saveToStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS, configs);
  return newConfig;
}

/**
 * Obtiene todas las configuraciones de balanceador
 * @returns {Array} Configuraciones
 */
export function getBalanceadorConfigs() {
  return getFromStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS);
}

/**
 * Guarda configuración de hotspot
 * @param {object} config - Configuración
 * @returns {object} Configuración guardada con ID
 */
export function saveHotspotConfig(config) {
  const configs = getFromStorage(STORAGE_KEYS.HOTSPOT_CONFIGS);
  const newConfig = {
    ...config,
    id: generateId(),
    createdAt: new Date().toISOString()
  };
  configs.push(newConfig);
  saveToStorage(STORAGE_KEYS.HOTSPOT_CONFIGS, configs);
  return newConfig;
}

/**
 * Obtiene todas las configuraciones de hotspot
 * @returns {Array} Configuraciones
 */
export function getHotspotConfigs() {
  return getFromStorage(STORAGE_KEYS.HOTSPOT_CONFIGS);
}

/**
 * Guarda configuración de failover
 * @param {object} config - Configuración
 * @returns {object} Configuración guardada con ID
 */
export function saveFailoverConfig(config) {
  const configs = getFromStorage(STORAGE_KEYS.FAILOVER_CONFIGS);
  const newConfig = {
    ...config,
    id: generateId(),
    createdAt: new Date().toISOString()
  };
  configs.push(newConfig);
  saveToStorage(STORAGE_KEYS.FAILOVER_CONFIGS, configs);
  return newConfig;
}

/**
 * Obtiene todas las configuraciones de failover
 * @returns {Array} Configuraciones
 */
export function getFailoverConfigs() {
  return getFromStorage(STORAGE_KEYS.FAILOVER_CONFIGS);
}

/**
 * Guarda configuración de QoS
 * @param {object} config - Configuración
 * @returns {object} Configuración guardada con ID
 */
export function saveQoSConfig(config) {
  const configs = getFromStorage(STORAGE_KEYS.QOS_CONFIGS);
  const newConfig = {
    ...config,
    id: generateId(),
    createdAt: new Date().toISOString()
  };
  configs.push(newConfig);
  saveToStorage(STORAGE_KEYS.QOS_CONFIGS, configs);
  return newConfig;
}

/**
 * Obtiene todas las configuraciones de QoS
 * @returns {Array} Configuraciones
 */
export function getQoSConfigs() {
  return getFromStorage(STORAGE_KEYS.QOS_CONFIGS);
}

/**
 * Guarda configuración de portal
 * @param {object} config - Configuración
 * @returns {object} Configuración guardada con ID
 */
export function savePortalConfig(config) {
  const configs = getFromStorage(STORAGE_KEYS.PORTAL_CONFIGS);
  const newConfig = {
    ...config,
    id: generateId(),
    createdAt: new Date().toISOString()
  };
  configs.push(newConfig);
  saveToStorage(STORAGE_KEYS.PORTAL_CONFIGS, configs);
  return newConfig;
}

/**
 * Obtiene todas las configuraciones de portal
 * @returns {Array} Configuraciones
 */
export function getPortalConfigs() {
  return getFromStorage(STORAGE_KEYS.PORTAL_CONFIGS);
}

// ==================== HISTORIAL DE SCRIPTS ====================

/**
 * Guarda script en historial
 * @param {object} data - { script, type, config, nombre }
 * @returns {object} Entrada de historial
 */
export function saveToHistory(data) {
  const history = getFromStorage(STORAGE_KEYS.SCRIPT_HISTORY);
  const entry = {
    id: generateId(),
    script: data.script,
    type: data.type,
    config: data.config,
    nombre: data.nombre || `Script ${data.type}`,
    createdAt: new Date().toISOString()
  };
  
  // Agregar al inicio
  history.unshift(entry);
  
  // Mantener solo los últimos 100
  if (history.length > 100) {
    history.length = 100;
  }
  
  saveToStorage(STORAGE_KEYS.SCRIPT_HISTORY, history);
  return entry;
}

/**
 * Obtiene todo el historial de scripts
 * @param {number} limit - Límite de resultados (default: 50)
 * @returns {Array} Historial
 */
export function getScriptHistory(limit = 50) {
  const history = getFromStorage(STORAGE_KEYS.SCRIPT_HISTORY);
  return history.slice(0, limit);
}

/**
 * Busca en el historial por tipo
 * @param {string} type - Tipo de script
 * @returns {Array} Scripts filtrados
 */
export function getHistoryByType(type) {
  const history = getFromStorage(STORAGE_KEYS.SCRIPT_HISTORY);
  return history.filter(item => item.type === type);
}

/**
 * Elimina una entrada del historial
 * @param {string} id - ID de la entrada
 */
export function deleteHistoryEntry(id) {
  const history = getFromStorage(STORAGE_KEYS.SCRIPT_HISTORY);
  const filtered = history.filter(item => item.id !== id);
  saveToStorage(STORAGE_KEYS.SCRIPT_HISTORY, filtered);
}

// ==================== ELIMINACIÓN DE DATOS ====================

/**
 * Elimina todas las configuraciones de balanceador
 */
export function deleteAllBalanceadorConfigs() {
  saveToStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS, []);
}

/**
 * Elimina todas las configuraciones de hotspot
 */
export function deleteAllHotspotConfigs() {
  saveToStorage(STORAGE_KEYS.HOTSPOT_CONFIGS, []);
}

/**
 * Elimina todas las configuraciones de failover
 */
export function deleteAllFailoverConfigs() {
  saveToStorage(STORAGE_KEYS.FAILOVER_CONFIGS, []);
}

/**
 * Elimina todas las configuraciones de QoS
 */
export function deleteAllQoSConfigs() {
  saveToStorage(STORAGE_KEYS.QOS_CONFIGS, []);
}

/**
 * Elimina todas las configuraciones de portal
 */
export function deleteAllPortalConfigs() {
  saveToStorage(STORAGE_KEYS.PORTAL_CONFIGS, []);
}

/**
 * Elimina todo el historial de scripts
 */
export function deleteAllHistory() {
  saveToStorage(STORAGE_KEYS.SCRIPT_HISTORY, []);
}

/**
 * Elimina TODO (excepto licencias)
 */
export function deleteAllData() {
  deleteAllBalanceadorConfigs();
  deleteAllHotspotConfigs();
  deleteAllFailoverConfigs();
  deleteAllQoSConfigs();
  deleteAllPortalConfigs();
  deleteAllHistory();
}

// ==================== EXPORTAR/IMPORTAR ====================

/**
 * Exporta todos los datos a un objeto
 * @returns {object} Datos completos
 */
export function exportAllData() {
  return {
    version: '2.0.0',
    exportDate: new Date().toISOString(),
    data: {
      balanceador: getFromStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS),
      hotspot: getFromStorage(STORAGE_KEYS.HOTSPOT_CONFIGS),
      failover: getFromStorage(STORAGE_KEYS.FAILOVER_CONFIGS),
      qos: getFromStorage(STORAGE_KEYS.QOS_CONFIGS),
      portal: getFromStorage(STORAGE_KEYS.PORTAL_CONFIGS),
      history: getFromStorage(STORAGE_KEYS.SCRIPT_HISTORY)
    }
  };
}

/**
 * Importa datos desde un objeto
 * @param {object} data - Datos a importar
 */
export function importAllData(data) {
  if (!data || !data.data) {
    throw new Error('Formato de datos inválido');
  }
  
  try {
    if (data.data.balanceador) {
      saveToStorage(STORAGE_KEYS.BALANCEADOR_CONFIGS, data.data.balanceador);
    }
    if (data.data.hotspot) {
      saveToStorage(STORAGE_KEYS.HOTSPOT_CONFIGS, data.data.hotspot);
    }
    if (data.data.failover) {
      saveToStorage(STORAGE_KEYS.FAILOVER_CONFIGS, data.data.failover);
    }
    if (data.data.qos) {
      saveToStorage(STORAGE_KEYS.QOS_CONFIGS, data.data.qos);
    }
    if (data.data.portal) {
      saveToStorage(STORAGE_KEYS.PORTAL_CONFIGS, data.data.portal);
    }
    if (data.data.history) {
      saveToStorage(STORAGE_KEYS.SCRIPT_HISTORY, data.data.history);
    }
  } catch (error) {
    throw new Error(`Error al importar datos: ${error.message}`);
  }
}

// ==================== UTILIDADES ====================

/**
 * Obtiene tamaño usado del almacenamiento
 * @returns {object} { used, total, percentage }
 */
export function getStorageInfo() {
  let used = 0;
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      used += localStorage[key].length + key.length;
    }
  }
  
  const usedKB = (used / 1024).toFixed(2);
  const totalKB = 5120; // ~5MB típico para localStorage
  const percentage = ((used / (totalKB * 1024)) * 100).toFixed(1);
  
  return {
    used: `${usedKB} KB`,
    total: `${totalKB} KB`,
    percentage: `${percentage}%`
  };
}

/**
 * Verifica si hay espacio disponible
 * @returns {boolean} true si hay espacio
 */
export function hasStorageSpace() {
  const info = getStorageInfo();
  return parseFloat(info.percentage) < 90; // Menos del 90% usado
}
